import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardBody } from '@progress/kendo-react-layout';
import './Login.scss';

import { useAuth } from '../../Context/AuthContext';
import { AuthServerLoginButton } from '../Components/AuthServerLoginButton';
import smartAppsLogoLight from '../../../../assets/images/sm/smart-apps-logo-light.png';
import smartAppsLogoDark from '../../../../assets/images/sm/smart-apps-logo-dark.png';
import { setLoginSession } from '../Utils/CommonUtil';
import { getUserInfo } from '../Services/LoginService';
import useIsDark from '../../Components/Theme/useIsDark';
import { Loader2 } from 'lucide-react';

export default function LoginPage() {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser, updateTenant,setAuthLoader,authLoader } = useAuth();
  const hasRedirected = useRef(false);
  const ssoRedirectedRef = useRef(false);
  const isDark = useIsDark();

  // If Login is rendered inline by AuthGaurd on a protected route (e.g. user refreshed `/abc`),
  // preserve that URL instead of forcing a redirect to `/` after session bootstrap.
  const redirectTo =
    searchParams.get('redirectTo') ||
    (typeof window !== 'undefined' && window.location
      ? `${window.location.pathname}${window.location.search || ''}`
      : '/');
  const message = searchParams.get('message');
  const errorParam = searchParams.get('error');
  const loggedOutParam = searchParams.get('logged_out');
  const isLoggedOut = loggedOutParam === '1';

  // Redirect if already logged in
  useEffect(() => {
    if (user && !hasRedirected.current) {
      hasRedirected.current = true;
      const path =
        typeof window !== 'undefined' && window.location
          ? window.location.pathname
          : '';
      if (path === '/login') {
        navigate(redirectTo, { replace: true });
      }
      // If Login is rendered inline by AuthGuard on a protected route,
      // let AuthGuard render the requested page without forcing navigation.
    }
  }, [user, redirectTo, navigate]);

  // Handle query messages
  useEffect(() => {
    if (message === 'signup-success') {
      setSuccess(
        'Account created successfully! Please check your email to verify your account.',
      );
    }

    if (loggedOutParam === '1') {
      setSuccess('You have been signed out successfully.');
    }

    if (errorParam) {
      switch (errorParam) {
        case 'verification-failed':
          setError(
            'Email verification failed. Please try signing up again or contact support.',
          );
          break;
        case 'session-expired':
          setError('Your session has expired. Please sign in again.');
          break;
        case 'loading-timeout':
          setError('Authentication timed out. Please try signing in again.');
          break;
        case 'auth-failed':
          setError('Authentication failed. Please try signing in again.');
          break;
        default:
          setError('An authentication error occurred. Please try again.');
      }
    }
  }, [message, errorParam, loggedOutParam]);

  // Check for existing session via userinfo endpoint
  useEffect(() => {
    const checkUserSession = async () => {
      try {
        // If we just logged out, skip the session check to avoid confusing messaging.
        if (isLoggedOut) {
          setIsLoading(false);
          return;
        }

        setIsLoading(true);

        const path =
          typeof window !== 'undefined' ? window.location.pathname : '';

        // Skip check if we're on certain pages
        const skipPaths = ['/forgot-password', '/new-password', '/register'];
        if (skipPaths.some((p) => path.startsWith(p))) {
          setIsLoading(false);
          return;
        }
        const userInfoResponse = await getUserInfo();
        if (userInfoResponse?.IsSuccess) {
          const userData = userInfoResponse?.Payload;
          // Set up login session with userinfo data
          try {
            setAuthLoader(true);
            await setLoginSession({
              userData,
              updateUser,
              updateTenant,
              page: 'login',
              setAuthLoader,
              redirectTo,
            });
            setIsLoading(false);
          } catch (err) {
            console.error('Error setting up login session:', err);
            setError('Failed to establish session. Please sign in again.');
            setIsLoading(false);
          }
        } else {
          // No valid session.
          // If user did not explicitly just log out, auto-redirect to Auth Server
          // when the session-check returns unauthorized.
          const status = userInfoResponse?.StatusCode;
          const isUnauthorized = status === 401 || status === 403;
          const loginUrl = process.env.OIDC_LOGIN_URL;

          if (!isLoggedOut && isUnauthorized && loginUrl && !ssoRedirectedRef.current) {
            ssoRedirectedRef.current = true;
            window.location.href = loginUrl;
            return;
          }

          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error checking user session:', error);
        setIsLoading(false);
      }
    };

    checkUserSession();
  }, [isLoggedOut]);

  return (
    <div className='sso-login'>
      <div className='sso-login__inner'>
        <Card className='sso-login__card'>
          <CardBody
            className={
              isLoading
                ? 'sso-login__cardContent sso-login__loading'
                : 'sso-login__cardContent'
            }
          >
            {isLoading ? (
              <>
                <Loader2 className='sso-login__pageSpinner' />
                <h1 className='sso-login__loadingTitle'>Signing you in</h1>
                <p className='sso-login__loadingSub'>Checking your session…</p>
              </>
            ) : (
              <>
                <img
                  src={isDark ? smartAppsLogoDark : smartAppsLogoLight}
                  alt={`${process.env.APP_NAME} Logo`}
                  className='sso-login__logoImg'
                />

                <p className='sso-login__subtitle'>Use your account to continue</p>

                {error && <div className='sso-login__errorBox'>{error}</div>}
                {success && <div className='sso-login__successBox'>{success}</div>}

                <AuthServerLoginButton
                  redirectTo={redirectTo}
                  label='Sign in'
                  className='sso-login__button'
                />
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
